import Event from '../utils/event';
import {SYSTEM_EVENTS} from '../events/events';
import BaseObject from '../base/base_object';
import DOM from '../utils/dom';
import _ from '../utils/underscore';
import ExternalLinks from '../components/external_links/external_links_component_controller';
import Clock from '../components/clock/clock_controller_component';
import LastLogin from '../components/last_login_component_wrapper/last_login_component_wrapper';
import GameJackpot from '../components/live_feeds/game_jackpot/game_jackpot_component_controller';
import SessionTimer from '../components/session_timer/session_timer_controller';
import ResponsibleGamingIconsComponentWrapper from '../components/responsible_gaming_icons_wrapper/responsible_gaming_icons_component_wrapper';
import AgeResponsibleGameComponent from "../components/regulation_components/base/age/age_responsible_game_component";
import {ComponentRenderer} from '../base/component_renderer';

class Directive extends BaseObject {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'Directive';


    /**
     *
     * @type {string}
     * @private
     */
    _prefix = 'cgp-directive-';


    /**
     *
     * @type {string[]}
     * @private
     */
    _cgpAllowedQuery = [
        {selector : 'id', getValueSelector: 'id'},
        {selector : 'class', getValueSelector: 'className'}
    ];


    /**
     * @property
     * @type {Array}
     */
    cgpLoadedDirectives = [];


    static table = {
        external_link           : {ref: ExternalLinks, isReRenderable: true},
        clock                   : {ref: Clock, isReRenderable: true},
        last_login              : {ref: LastLogin, isReRenderable: true},
        game_jackpot            : {ref: GameJackpot, isReRenderable: true},
        session_timer           : {ref: SessionTimer, isReRenderable: true},
        responsible_gaming_icons: {ref: ResponsibleGamingIconsComponentWrapper, isReRenderable: true},
        age_responsible_game    : {ref: AgeResponsibleGameComponent, isReRenderable: true}
    };


    static createComponentRenderer(componentId) {
        let componentDescriptor = Directive.table[componentId];
        if (!componentDescriptor) {
            throw new Error(`There is no sdk component with id ${componentId}`);
        }
        return new ComponentRenderer(componentDescriptor.ref);
    }

    CLEAN_DIRECTIVES_INTERVAL = 60000;


    /**
     *
     * @param params
     */
    init(params) {
        this
            ._loadDirectives(params)
            ._subscribe()
            .render()
            ._setTimeoutRemoveDirectives()
            ._initDOMObserver(document.body, params);
    }


    /**
     * Generate the cgp directives.
     *
     * @param componentOptions
     * @returns {*}
     */
    static generate(componentOptions) {
        let directiveRaw = Directive.table[componentOptions.directiveName];

        if (_.isUndefined(directiveRaw)) {
            console.error('Directive name => ' + componentOptions.directiveName + ' not allowed in the directives table.');
            return null;
        }

        //this._loadDirective
        let directiveInstance = directiveRaw.ref.create(componentOptions.props);

        if (_.isUndefined(directiveInstance)) {
            console.error('Invalid directive could not create instance error.');
            return null;
        }

        // If the programmer choose to auto start the component and set a start true flag.
        if (componentOptions.start) {
            directiveInstance.start();
        }

        return directiveInstance;
    }


    /**
     * Render the loaded directives into dom.
     *
     * @returns {Directive}
     * @private
     */
    render(forceRender = true) {
        this.cgpLoadedDirectives.forEach((directive) => {
            if (!forceRender) {
                this.logger.debug('Should consider and not re render the not allowed re render directive because using the force render flag to => ', forceRender)

                if (!directive.isReRenderable) {
                    this.logger.debug('Directive is not allowed to be re render => ', directive.options.name);
                    return;
                }
            }
            this._drawDirective(directive);
        });
        return this;
    }


    /**
     *
     * @param directive
     * @private
     */
    _drawDirective(directive) {
        // Start the directive to build himself.
        directive.directiveInstance.start();

        if (!directive.options.isOnDOM) {
            // // Clear previous directive DOM html.
            directive.options.el.innerHTML = '';
            // Append new bake directive element on the dom directive.
            DOM.append(directive.options.el, directive.directiveInstance.getEl());
            // Set that the directive is appended on dom.
            directive.options.isOnDOM = true;
        }

        Event.fire(SYSTEM_EVENTS.ELEMENT_CREATED, directive.options.el);
        directive.directiveInstance.onAppendedToDOM();
        //this.logger.debug('Draw component into the dom --> ', directive.options.name);
    }


    /**
     * Load single directive
     * @param cgpElement
     * @param query
     * @param params
     * @private
     */
    _loadDirective(cgpElement, query, params) {
        let cgpDirectiveOptions = this._makeDirectiveOptions(cgpElement, query);
        if (_.isNull(cgpDirectiveOptions)) {
            return;
        }

        let directiveRef = cgpDirectiveOptions.directive.ref;
        let directive = directiveRef.create(Object.assign({},
            cgpDirectiveOptions.configuration, // Directive dom configuration.
            params, // Params from loader.
            {isDirective: true} // Custom directive inner implementation.
        ));

        let loadedDirective = {
            directiveInstance: directive,
            options: cgpDirectiveOptions,
            isReRenderable: cgpDirectiveOptions.directive.isReRenderable
        };
        this.cgpLoadedDirectives.push(loadedDirective);

        return loadedDirective;
    }


    /**
     * Load all cgp directive and set them into directive loaded array.
     *
     * @returns {object}
     * @public
     */
    _loadDirectives(params) {
        this._cgpAllowedQuery.forEach((query) => {
            let cgpElements = DOM.$$('[' + query.selector + '^=' + this._prefix + ']');
            cgpElements.forEach((cgpElement) => {
                this._loadDirective(cgpElement, query, params);
            });
        });

        return this;
    }


    /**
     * Subscribe the directive to global application events.
     *
     * @return {Directive} this, for chaining.
     */
    _subscribe() {
        Event.on([
            SYSTEM_EVENTS.OAUTH_STATE_CHANGE
        ], () => {
              this._cleanDirectives();
            this.render(false);
        });

        return this;
    }


    /**
     * Handle add new element into the DOM
     * @param newNodes
     * @private
     */
    _onElementsAdded(elements, params) {
        if (false === elements || elements.length === 0) {
            return;
        }

        for(let i = 0; i < elements.length; i++) {
            let el = elements[i];
            if (el instanceof HTMLElement) {
                if (this._tryLoadDirective(el, params) === false) {
                    this._cgpAllowedQuery.forEach((query) => {
                        let cgpElements = el.querySelectorAll('[' + query.selector + '^=' + this._prefix + ']');
                        cgpElements.forEach((cgpElement) => {
                            this._tryLoadDirective(cgpElement, params);
                        });
                    });
                }
            }
        }
    }


    /**
     * Try load dynamic directive
     * @param el
     * @param params
     * @returns {boolean}
     * @private
     */
    _tryLoadDirective(el, params) {
        let query = this._getCGPDirectiveQuery(el);
        if (query) {
            //test if this directive is already loaded
            let componentSelector = this._getDynamicDirectiveSelector(el);
            if (componentSelector) {
                this.logger.warn('Directive is ' + componentSelector + ' has already loaded');
                return false;
            }

            let loadedDirective = this._loadDirective(el, query, params);
            this._drawDirective(loadedDirective);
            return true
        }
        return false;
    }


    /**
     * Get component selector into the directive element
     * @param el
     * @returns {null}
     * @private
     */
    _getDynamicDirectiveSelector(el) {
        if (el && el.children.length > 0) {
            let selector = el.children[0].className.match(/cgp-(\w*-?)*component/g);
            return selector;
        }

        return null;
    }


    /**
     * Create CGP query selector to element
     * @param el
     * @returns {*}
     * @private
     */
    _getCGPDirectiveQuery(el) {
        let query;
        if (el instanceof HTMLElement) {
            if (el.getAttribute('id') && el.getAttribute('id').indexOf(this._prefix) > -1) {
                query = {selector: 'id', getValueSelector: 'id'};
            } else if (el.getAttribute('class') && el.getAttribute('class').indexOf(this._prefix) > -1) {
                query = {selector: 'class', getValueSelector: 'className'}
            }
        }

        return query;
    }


    /**
     * Initialize listener for DOM changes
     * @param observerRoot
     * @returns {Directive}
     * @private
     */
    _initDOMObserver(observerRoot, params) {
        let MutationObserver = window.MutationObserver || window.WebKitMutationObserver,
            eventListenerSupported = window.addEventListener;

        if (MutationObserver) {
            // define a new observer
            this._obs = new MutationObserver((mutations) => {
                mutations.forEach((record) => {
                    if (record.addedNodes.length > 0) {
                        //this.logger.debug(record);
                        this._onElementsAdded(record.addedNodes, params);
                    }
                });

            });
            // have the observer observe foo for changes in children
            this._obs.observe(observerRoot, {childList: true, subtree: true});
        } else
        if (eventListenerSupported) {
            DOM.addEventListener(observerRoot, 'DOMNodeInserted', this._onDOMNodeInserted, false);
        }

        return this;
    }


    /**
     * On DOM node inserted callback.
     */
    _onDOMNodeInserted(mutationEvent, params) {
        let elements = [];
        elements.push(mutationEvent.target);
        this._onElementsAdded(elements, params);
    }


    /**
     *
     * @returns {Directive}
     * @private
     */
    _setTimeoutRemoveDirectives() {
        this.cleanDirectivesTimeout = DOM.setTimeout(() => {
            this.logger.debug('Clean directives loop started');
            this._cleanDirectives();
            this._setTimeoutRemoveDirectives();
        }, this.CLEAN_DIRECTIVES_INTERVAL);

        return this;
    }

    /**
     * clean directives which were removed from dome
     * @returns {Directive}
     * @private
     */
    _cleanDirectives() {
        for (let i = this.cgpLoadedDirectives.length - 1; i >= 0; i--) {
            let directive = this.cgpLoadedDirectives[i];
            let el = directive.directiveInstance.getEl();
            if (_.isUndefined(el.offsetParent) || _.isNull(el.offsetParent)) {
                this.cgpLoadedDirectives.splice(i, 1);
                directive.directiveInstance.shutDown();
                this.logger.debug('Directive ' + directive.options.name + ' is not used and has been removed');
            }
        }

        return this;
    }

    /**
     * shutDown all existing directives instances
     * @returns {Directive}
     * @private
     */
    _shutDownDirectives() {
        for (let i = this.cgpLoadedDirectives.length - 1; i >= 0; i--) {
            let directive = this.cgpLoadedDirectives[i];
            let el = directive.directiveInstance.getEl();
            if (el.parentElement){
                el.parentElement.removeChild(el);
            }
            this.cgpLoadedDirectives.splice(i, 1);
            directive.directiveInstance.shutDown();
        }
    }


    /**
     * Prepare directive attribute for instance if is allowed in the directive rules.
     *
     * @param cgpElement
     * @param query
     * @private
     * @returns {{el: *, selector: *, configuration: {}, name: string}|null}
     */
    _makeDirectiveOptions(cgpElement, query) {
        let directiveName = cgpElement[query.getValueSelector].replace(this._prefix, '')
            .toLowerCase() // lower case
            .split(' ')[0]; // use only the first part, incase the directive has more properties in the classname.

        if (!Directive.table && !Directive.table[directiveName]) {
            this.logger.error('Sorry this dom element is not allowed in cgp directives => ', cgpElement);
            return null;
        }

        if (!Directive.table[directiveName]) {
            this.logger.error('Not found a match reference in cgp directive for --> ', directiveName);
            return null;
        }

        return {
            el: cgpElement,
            selector: query.selector,
            configuration: DOM.parseDataAttributes(cgpElement),
            name: directiveName,
            directive: Directive.table[directiveName],
            isOnDOM: false
        };
    }


    /**
     * Clean event subscription
     */
    shutDown() {
        this._shutDownDirectives();
        document.body.removeEventListener('DOMNodeInserted', this._onDOMNodeInserted);
        if (!_.isUndefined(this._obs)) {
            this._obs.disconnect();
        }

        if (!_.isUndefined(this.cleanDirectivesTimeout)) {
            DOM.clearTimeout(this.cleanDirectivesTimeout);
        }
    }
}

export default Directive;
